<template>
  <v-container
    fluid
    tag="section"
  >
    <v-card>
      <v-card-text>
        <v-row dense>
          <v-col
            cols="12"
            sm="8"
            class="d-sm-flex align-center"
          >
            <span class="text-subtitle-1">Точек: {{ rows.length }}</span>
          </v-col>
          <v-col
            cols="12"
            sm="4"
            class="d-sm-flex justify-end"
          >
            <v-btn
              icon
              title="Обновить"
              :loading="loading"
              @click="refresh()"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <v-data-table
          v-if="!loading"
          :headers="headers"
          :items="rows"
          :footer-props="datatable_footer"
          no-data-text="Нет данных для отображения"
        >
          <template v-slot:footer.page-text="{pageStart,pageStop,itemsLength}">
            {{ pageStart }}-{{ pageStop }} из {{ itemsLength }}
          </template>
          <template v-slot:item.passage_at="{item}">
            <div v-if="item.passage_at">
              {{ fmt(item.passage_at) }}
              <div class="text-caption grey--text">
                {{ ago(item.passage_at) }}
              </div>
            </div>
            <span v-else>—</span>
          </template>
          <template v-slot:item.adb_at="{item}">
            <div v-if="item.adb_at">
              {{ fmt(item.adb_at) }}
              <div class="text-caption grey--text">
                {{ ago(item.adb_at) }}
              </div>
            </div>
            <span v-else>—</span>
          </template>
          <template v-slot:item.online="{item}">
            <v-chip
              small
              :color="isOnline(item) ? 'green' : 'red'"
              text-color="white"
            >
              {{ isOnline(item) ? 'онлайн' : 'офлайн' }}
            </v-chip>
          </template>
        </v-data-table>
        <v-row
          v-else
          dense
          class="justify-center py-12"
        >
          <v-progress-circular
            indeterminate
            color="primary"
            size="48"
          />
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import moment from 'moment'
import { loadPlaces } from '@/views/skud/shared/services/skud-places-service'
import { SkudPointService } from '@/views/skud/shared/services/skud-point-service'
import { SkudPassageService } from '@/views/skud/shared/services/skud-passage-service'
import { MonitoringPaginationService } from '@/views/monitoring/shared/services/monitoring-pagination-service'

const DATE_FMT = 'DD.MM.YYYY HH:mm:ss'
const ONLINE_MS = 10 * 60 * 1000
const CHUNK = 5
// ponytail: потолок 5 страниц проходов (1000 записей); при большем парке хвосты покажут «—»
const MAX_PASSAGE_PAGES = 5

export default {
  name: 'SkudDashboard',
  data () {
    return {
      rows: [],
      loading: false,
      points: null,
      passages: null,
      monitoring: null,
      datatable_footer: {
        itemsPerPageText: 'Показывать по',
        pageText: 'из',
        itemsPerPageOptions: [5, 10, 15, 30, 50]
      },
      headers: [
        { text: 'Место', value: 'place_name' },
        { text: 'Точка', value: 'point_name' },
        { text: 'Направление', value: 'direction_text' },
        { text: 'Последний проход', value: 'passage_at' },
        { text: 'Мониторинговый сигнал', value: 'adb_at' },
        { text: 'Онлайн', value: 'online', sortable: false }
      ]
    }
  },
  created () {
    this.points = new SkudPointService()
    this.passages = new SkudPassageService()
    this.monitoring = new MonitoringPaginationService()
    this.refresh()
  },
  methods: {
    fmt (v) {
      return moment(v).format(DATE_FMT)
    },
    ago (v) {
      const mins = moment().diff(moment(v), 'minutes')
      if (mins < 1) return 'только что'
      if (mins < 60) return `${mins} мин назад`
      const hours = moment().diff(moment(v), 'hours')
      if (hours < 24) return `${hours} ч назад`
      return `${moment().diff(moment(v), 'days')} д назад`
    },
    isOnline (item) {
      return !!item.adb_at && (Date.now() - moment(item.adb_at).valueOf()) < ONLINE_MS
    },
    async mapInChunks (items, fn) {
      for (let i = 0; i < items.length; i += CHUNK) {
        await Promise.all(items.slice(i, i + CHUNK).map(fn))
      }
    },
    async refresh () {
      this.loading = true
      try {
        const places = await loadPlaces()
        const rows = []
        await this.mapInChunks(places, async (place) => {
          const points = await this.points.getPoints(place.value)
          for (const p of points) {
            rows.push({
              place_name: place.text,
              point_id: p.id,
              point_name: p.name,
              direction_text: p.direction_text,
              passage_at: null,
              adb_at: null
            })
          }
        })
        this.rows = rows
        await Promise.all([this.applyPassages(rows), this.applyAdb(rows)])
      } finally {
        this.loading = false
      }
    },
    // Проходы отсортированы по occurred_at DESC — первый встреченный проход точки и есть последний.
    async applyPassages (rows) {
      const byPoint = {}
      for (const r of rows) byPoint[r.point_id] = r
      const need = new Set(rows.map(r => r.point_id))
      let cursors = {}
      for (let page = 0; page < MAX_PASSAGE_PAGES && need.size; page++) {
        const res = await this.passages.getPassages(null, {}, cursors)
        for (const it of res.items) {
          if (need.delete(it.point_id)) byPoint[it.point_id].passage_at = it.occurred_at_raw
        }
        if (!res.next_cursor_id) break
        cursors = res
      }
    },
    async applyAdb (rows) {
      // ponytail: фильтр по текущей дате клиента (timezone точки во фронтенде неизвестна);
      // в пограничные часы суток при tz точки ≠ tz клиента последняя запись может не попасть — тогда слать tz точки
      const today = moment().format('YYYY-MM-DD')
      await this.mapInChunks(rows, async (row) => {
        try {
          const res = await this.monitoring.getAdb({ pointTimeZone: 0, pointId: row.point_id, dateFrom: today, dateTo: today, page: 1, limit: 1 })
          // при ошибке endpoint возвращает заглушку с текущей датой при count=0 — верим только count>0
          const d = res && res.count > 0 && res.adbLogs && res.adbLogs[0] && res.adbLogs[0].date
          if (d) row.adb_at = d
        } catch (e) {
          // точка без данных/ошибка — показываем «—», остальная таблица живая
        }
      })
    }
  }
}
</script>
